export const NESTIFYZ_OVERVIEW_TOPIC_NAME = "Overview";
export const NESTIFYZ_COMPONENTS_TOPIC_NAME = "Components";
export const NESTIFYZ_OVERVIEW_MARKER = "Nestifyz Overview Import";
export const NESTIFYZ_COMPONENTS_MARKER = "Nestifyz Components Import";

/**
 * @param {import('./parseExport').NestiFyzGame} game
 * @returns {boolean}
 */
export const hasImportableOverviewTopic = (game) => {
  if (game.complexity?.trim() || game.languageDependency?.trim() || game.inspiredBy?.trim()) {
    return true;
  }

  if ((game.scoreBasedAttributes || []).some((attribute) => attribute.attributeName)) {
    return true;
  }

  const uspFields = [
    game.uniqueSellingPoint1Title,
    game.uniqueSellingPoint1,
    game.uniqueSellingPoint2Title,
    game.uniqueSellingPoint2,
    game.uniqueSellingPoint3Title,
    game.uniqueSellingPoint3,
  ];

  return uspFields.some((value) => typeof value === "string" && value.trim().length > 0);
};

/**
 * @param {import('./parseExport').NestiFyzGame} game
 * @returns {boolean}
 */
export const hasImportableComponentsTopic = (game) => {
  return !!(game.components?.trim() || game.estimatedCosts?.trim());
};

/**
 * @param {string|undefined} title
 * @param {string|undefined} body
 * @param {string} fallbackTitle
 * @returns {string|undefined}
 */
const buildSellingPointSection = (title, body, fallbackTitle) => {
  const trimmedBody = body?.trim();

  if (!trimmedBody) {
    return undefined;
  }

  const heading = title?.trim() || fallbackTitle;

  return `## ${heading}\n\n${trimmedBody}`;
};

/**
 * @param {import('./parseExport').NestiFyzScoreBasedAttribute[]} [attributes]
 * @returns {string|undefined}
 */
const buildScoreBasedAttributesSection = (attributes = []) => {
  const lines = attributes
    .filter((attribute) => attribute.attributeName)
    .map((attribute) => {
      const value = attribute.attributeValue;

      if (value === undefined || value === null) {
        return `- **${attribute.attributeName}**`;
      }

      return `- **${attribute.attributeName}:** ${value}`;
    });

  if (lines.length === 0) {
    return undefined;
  }

  return `## Score Based Attributes\n\n${lines.join("\n")}`;
};

/**
 * @param {import('./parseExport').NestiFyzGame} game
 * @returns {string}
 */
export const buildNestiFyzOverviewTopicBody = (game) => {
  const sections = [NESTIFYZ_OVERVIEW_MARKER];

  if (game.complexity?.trim()) {
    sections.push(`## Complexity\n\n${game.complexity.trim()}`);
  }

  if (game.languageDependency?.trim()) {
    sections.push(`## Language Dependency\n\n${game.languageDependency.trim()}`);
  }

  const scoreSection = buildScoreBasedAttributesSection(game.scoreBasedAttributes);
  if (scoreSection) {
    sections.push(scoreSection);
  }

  const usp1 = buildSellingPointSection(
    game.uniqueSellingPoint1Title,
    game.uniqueSellingPoint1,
    "Unique Selling Point 1"
  );
  if (usp1) {
    sections.push(usp1);
  }

  const usp2 = buildSellingPointSection(
    game.uniqueSellingPoint2Title,
    game.uniqueSellingPoint2,
    "Unique Selling Point 2"
  );
  if (usp2) {
    sections.push(usp2);
  }

  const usp3 = buildSellingPointSection(
    game.uniqueSellingPoint3Title,
    game.uniqueSellingPoint3,
    "Unique Selling Point 3"
  );
  if (usp3) {
    sections.push(usp3);
  }

  if (game.inspiredBy?.trim()) {
    sections.push(`## Inspired By\n\n${game.inspiredBy.trim()}`);
  }

  if (game.id) {
    sections.push(`Nestifyz Game Id: ${game.id}`);
  }

  return sections.join("\n\n");
};

/**
 * @param {import('./parseExport').NestiFyzGame} game
 * @returns {string}
 */
export const buildNestiFyzComponentsTopicBody = (game) => {
  const sections = [NESTIFYZ_COMPONENTS_MARKER];

  if (game.components?.trim()) {
    sections.push(`## Components\n\n${game.components.trim()}`);
  }

  if (game.estimatedCosts?.trim()) {
    sections.push(`## Estimated Costs\n\n${game.estimatedCosts.trim()}`);
  }

  if (game.id) {
    sections.push(`Nestifyz Game Id: ${game.id}`);
  }

  return sections.join("\n\n");
};

/**
 * @param {Array<{name: string, archived: boolean}>} topics
 * @param {string} topicName
 * @returns {{name: string, archived: boolean}|undefined}
 */
export const findExistingImportedGameTopic = (topics, topicName) => {
  return topics.find((topic) => topic.name === topicName && !topic.archived);
};
